/**
 * Lädt die ELiA-Wertelisten für die Auswahlfelder der Stoffliste und des Auffangraums.
 *
 * Die zehn kurzen Listen kommen in einem Abruf (zusammen unter 120 Einträge) und werden hier
 * einmal in Dropdown-Optionen umgesetzt. Die beiden großen Kataloge – AVV mit 842 und CAS mit
 * 3.117 Einträgen – sind bewusst nicht dabei: Sie werden über die Suchendpunkte eingegrenzt,
 * statt bei jedem Öffnen eines Dialogs vollständig in den Browser zu wandern.
 */
import { useCallback, useEffect, useState } from "react";
import {
  anlagentypenApi,
  Wertelisten,
  WertelisteWert,
} from "../../../../services/api/anlagentypen";

/** Struktur, die `Dropdown` erwartet – `DropdownOption` selbst wird dort nicht exportiert. */
export interface Option {
  value: string;
  label: string;
}

export type WertelistenOptions = Record<keyof Wertelisten, Option[]>;

const LEER: WertelistenOptions = {
  einheit: [],
  entsorgungsverfahren: [],
  aggregatzustand: [],
  gefaehrdungsstufe: [],
  reach_rolle: [],
  awsv_einstufung: [],
  rueckhaltung_art: [],
  dichtheitspruefung_intervall: [],
  umgang_art: [],
  einsatzzeit_bezug: [],
};

/**
 * Gespeichert wird die ID, angezeigt die Bezeichnung. Der ELiA-Code steht in Klammern dahinter,
 * weil er im Schriftverkehr mit der Behörde auftaucht – bei den Einheiten ist „kg" die Angabe,
 * die im Antrag landet, nicht „Kilogramm".
 */
const alsOptionen = (werte: WertelisteWert[]): Option[] =>
  werte.map((w) => ({ value: w.id, label: `${w.name} (${w.code})` }));

export const useWertelisten = () => {
  const [wertelisten, setWertelisten] = useState<WertelistenOptions>(LEER);
  const [loading, setLoading] = useState(false);

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const daten = await anlagentypenApi.getWertelisten();
      setWertelisten(
        Object.fromEntries(
          Object.entries(daten).map(([name, werte]) => [
            name,
            alsOptionen(werte as WertelisteWert[]),
          ]),
        ) as WertelistenOptions,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  return { wertelisten, loading, reload: laden };
};
